import { defineStore } from 'pinia';
import type { FetchError } from 'ofetch';
import type { BackendCouponInfoResponse } from '~/lib/shop-api';
import type { ProductProviderKey } from '#shared/types/goliath/goliath.types';

export type ReductionMethod =
  | 'RS' // Discount amount, single unit (Voucher)
  | 'RT' // Discount amount, single unit (Coupon)
  | 'RP' // Percent
  | 'PS'; // New price, single unit

export type BaseCoupon = {
  description?: string;
  reductionMethodCode?: ReductionMethod;
  status:
    'empty' | 'VALID' | 'PARTIALREDEEMED' | 'brandError' | 'INVALID' | 'EXPIRED' | 'redeemError';
  productNumbersWhitelist?: string[];
  productNumbersBlacklist?: string[];
  providersWhitelist?: ('EGSolo_Brodos' | 'EGSolo_Telekom')[];
  providersBlacklist?: ('EGSolo_Brodos' | 'EGSolo_Telekom')[];
  reductionValue?: number;
  type?: 'coupon' | 'voucher';
  typeCode?: '01' | '02'; // 01: apply-able on 1 product;
  initialValue?: number;
};

export interface Coupon extends BaseCoupon {
  couponCode: string;
  expiryDate?: string;
}

export interface RedeemedCouponResponse extends BaseCoupon {
  roundingMethodCode: string;
  multiple: string;
}

export interface CouponState {
  coupon: Coupon | undefined;
}

const couponErrorMessages: Record<string, string> = {
  invalid: 'Leider ist der eingegebene Code nicht gültig. Prüfe Deine Eingabe.',
  expired: 'Leider ist der eingegebene Code nicht mehr gültig.',
  redeemed: 'Der eingegebene Coupon/Gutschein wurde bereits eingelöst.',
  branderror: 'Der eingegebene Code ist nicht für diese Produkte gültig.',
  // keys are lowercase, because `errorStatus` is lowercased
  redeemerror:
    'Der Gutschein kann gerade nicht eingelöst werden, bitte versuche es später noch einmal!',
};

export const useCouponStore = defineStore('coupon', {
  state: (): CouponState => ({
    coupon: undefined,
  }),

  getters: {
    getCoupon(state): Coupon | undefined {
      return state.coupon;
    },
    compatibleArticleNumbers(state): string[] {
      return state.coupon?.productNumbersWhitelist || [];
    },
    incompatibleArticleNumbers(state): string[] {
      return state.coupon?.productNumbersBlacklist || [];
    },
    compatibleProductProviders(state): ProductProviderKey[] {
      if (!state.coupon) return [];
      if (
        state.coupon.providersWhitelist?.includes('EGSolo_Brodos') &&
        state.coupon.providersWhitelist?.includes('EGSolo_Telekom')
      ) {
        return [];
      }
      if (
        state.coupon.providersWhitelist?.includes('EGSolo_Telekom') ||
        state.coupon.providersBlacklist?.includes('EGSolo_Brodos')
      ) {
        return ['TELEKOM' as ProductProviderKey];
      }
      if (
        state.coupon.providersWhitelist?.includes('EGSolo_Brodos') ||
        state.coupon.providersBlacklist?.includes('EGSolo_Telekom')
      ) {
        return ['BRODOS' as ProductProviderKey];
      }
      return [];
    },
    isValidCoupon(state): boolean {
      return ['valid', 'partialredeemed'].includes(state.coupon?.status?.toLowerCase() || '');
    },
    errorStatus(state): string {
      return !this.isValidCoupon ? state.coupon?.status?.toLowerCase() || '' : '';
    },
    reductionValue(state): string | number | undefined {
      if (!state.coupon) return undefined;
      return state.coupon.reductionValue && state.coupon.reductionValue % 1 !== 0
        ? state.coupon.reductionValue.toFixed(2)
        : state.coupon.reductionValue;
    },
    couponErrorMessage(): string | undefined {
      return couponErrorMessages[this.errorStatus?.toLowerCase()];
    },
  },

  actions: {
    setCoupon(coupon: Coupon | undefined) {
      this.coupon = coupon;
    },
    setError(errorType: BaseCoupon['status']) {
      if (this.coupon) {
        this.coupon.status = errorType;
      }
    },

    async checkCouponCode(couponCode: string) {
      if (
        !couponCode ||
        (couponCode === this.coupon?.couponCode && this.coupon.status !== 'redeemError')
      )
        return;

      const data = await useShopApi()
        .getCouponInfo(couponCode)
        .catch((error: FetchError<BackendCouponInfoResponse>) => {
          this.setCoupon({
            status: error.data?.couponVoucher?.status || 'redeemError',
            couponCode,
          });

          throw error;
        });
      if (data && data.couponVoucher) {
        this.setCoupon({
          ...data.couponVoucher,
          couponCode,
        });
      } else {
        this.setCoupon(undefined);
      }
    },

    clearCoupon() {
      this.coupon = undefined;
    },

    reportBrandError() {
      this.setError('brandError');
    },
  },
});
